// apps/ui/src/niches/nicheData.js
// Programmatic SEO Database for High-Intent Retail & eCommerce Niches in India.
// Powers programmatic category landing pages at /create/:slug

export const NICHES = [
  {
    slug: 'clothing-website',
    nicheName: 'Clothing & Fashion Boutique',
    shortName: 'Clothing',
    icon: '👗',
    badge: 'Trending Fashion Niche',
    headline: 'How to Create a Clothing Website in 30 Seconds',
    subheadline: 'Launch your online clothing boutique with zero coding. Sell kurtis, sarees, lehengas, western dresses and co-ords with size charts, WhatsApp ordering, and instant UPI checkout.',
    metaTitle: 'How to Create a Clothing Website | Online Boutique Store – Ownezzy',
    metaDescription: 'Create an online clothing store website with size charts, color swatches, WhatsApp orders, 0% commission & instant UPI. Start your 7-day free trial today.',
    targetKeywords: [
      'how to create clothing website',
      'clothing store website builder',
      'sell clothes online india',
      'kurti saree online store',
      'online boutique website'
    ],
    templateId: 'clothing',
    demoSubdomain: 'fashion',
    accentColor: '#d6336c',
    heroStats: {
      activeStores: '2,400+',
      avgSetupTime: '30 seconds',
      commission: '0%',
      rating: '4.9/5'
    },
    keyBenefits: [
      {
        title: 'Size & Color Variants',
        desc: 'Offer XS to 5XL sizing and multiple color options with real-time stock counts so buyers get the exact fit.',
        icon: '📏'
      },
      {
        title: 'Direct WhatsApp Ordering',
        desc: 'Convert Instagram DMs and WhatsApp inquiries into automated checkout orders with address & payment slip.',
        icon: '💬'
      },
      {
        title: 'Instant UPI & COD Support',
        desc: 'Accept payments via PhonePe, Google Pay, Paytm, cards, or allow Cash on Delivery with phone confirmation.',
        icon: '⚡'
      },
      {
        title: 'Zero Commission Fees',
        desc: 'Unlike marketplaces charging 15-30% on every order, keep 100% of your boutique profit margins.',
        icon: '🏷️'
      }
    ],
    featuresList: [
      'Mobile-first responsive boutique storefront designed for Indian shoppers',
      'High-resolution multi-photo zoom and video reel showcase on product pages',
      'Automated WhatsApp shipping notifications with live tracking links',
      'Custom domain support (e.g. www.yourboutique.com) with free SSL certificate',
      'Built-in coupon codes, festival sale banners, and buy-more-save-more bundles',
      'One-click Instagram Bio link integration to convert followers into paying customers'
    ],
    howToSteps: [
      {
        step: '1',
        title: 'Choose the Clothing Boutique Template',
        desc: 'Select the pre-designed UrbanThread fashion theme optimized for dresses, sarees, and suits.'
      },
      {
        step: '2',
        title: 'Upload Your Collection & Set Sizes',
        desc: 'Add your photos, descriptions, prices, and size variants via phone or bulk Excel import.'
      },
      {
        step: '3',
        title: 'Share Your Link & Receive Orders',
        desc: 'Put your store link in your Instagram bio and WhatsApp status to get paid directly via UPI.'
      }
    ],
    faqList: [
      {
        q: 'How long does it take to create a clothing website with Ownezzy?',
        a: 'Your clothing website is live in under 30 seconds. Choose the clothing template, name your store, and your complete ecommerce store with shopping cart and WhatsApp checkout is ready.'
      },
      {
        q: 'Can I add multiple sizes like Small, Medium, Large, and XL?',
        a: 'Yes! Every product can have custom variant attributes including sizes (XS, S, M, L, XL, XXL, Free Size), color swatches, fabric type, and custom measurement notes.'
      },
      {
        q: 'Do I need a GST number or trade license to get started?',
        a: 'No GST is required to start your free trial and receive orders. As a small business or home boutique, you can start selling immediately with your savings account and UPI.'
      },
      {
        q: 'How do customers pay on my clothing store?',
        a: 'Customers can pay instantly via UPI (PhonePe, Google Pay, Paytm), debit/credit cards, net banking, or Cash on Delivery (COD) according to your preferences.'
      }
    ],
    relatedNiches: ['jewelry-website', 'footwear-website', 'beauty-cosmetics-website', 'kids-ethnic-wear-website']
  },

  {
    slug: 'jewelry-website',
    nicheName: 'Jewelry & Imitation Accessories',
    shortName: 'Jewelry',
    icon: '💎',
    badge: 'High-Margin Retail Niche',
    headline: 'How to Create a Jewelry Website in 30 Seconds',
    subheadline: 'Start selling artificial jewelry, fine gold-plated ornaments, oxidized silver, Kundan sets, and bridal jewelry online with high-res macro photos and direct WhatsApp checkout.',
    metaTitle: 'How to Create a Jewelry Website | Artificial & Bridal Jewelry Store – Ownezzy',
    metaDescription: 'Create an online jewelry website with macro photo zooms, anti-tarnish warranty badges, WhatsApp ordering, 0% commission & UPI. Start free 7-day trial today.',
    targetKeywords: [
      'how to create jewelry website',
      'imitation jewelry website builder',
      'sell jewelry online india',
      'artificial jewellery ecommerce store',
      'oxidized jewelry online shop'
    ],
    templateId: 'uruli',
    demoSubdomain: 'uruli',
    accentColor: '#b45309',
    heroStats: {
      activeStores: '1,800+',
      avgSetupTime: '30 seconds',
      commission: '0%',
      rating: '4.9/5'
    },
    keyBenefits: [
      {
        title: 'Macro Image Zoom & Video',
        desc: 'Display sparkling details, stones, and craftsmanship with crystal-clear high-definition photo zooms.',
        icon: '🔍'
      },
      {
        title: 'Care & Warranty Badges',
        desc: 'Highlight anti-tarnish guarantees, 1-gram gold plating certifications, and gift box packaging.',
        icon: '🛡️'
      },
      {
        title: 'Safe Prepaid UPI Checkout',
        desc: 'Encourage prepaid orders with instant UPI discounts to eliminate high COD returns and transit risks.',
        icon: '💎'
      },
      {
        title: 'Gifting & Return Favors',
        desc: 'Offer custom gift packaging, personal notes, and bulk order discounts for weddings & festive occasions.',
        icon: '🎁'
      }
    ],
    featuresList: [
      'Jewelry-first aesthetic showcasing Kundan, oxidized, American Diamond & temple jewellery',
      'Multi-angle photo carousels with close-up finish & model try-on shots',
      'Instant WhatsApp order sharing for customization requests & matching blouses',
      'Integrated pincode serviceability and insured courier tracking',
      'Automated invoice generation with product care instructions',
      'Zero marketplace listing deductions—100% of the sale goes to you'
    ],
    howToSteps: [
      {
        step: '1',
        title: 'Select the Luxury Jewelry Theme',
        desc: 'Pick an elegant, premium layout designed specifically for shimmering accessories.'
      },
      {
        step: '2',
        title: 'Add Jewelry Pieces & Plating Details',
        desc: 'Upload clear photos of earrings, necklaces, bangles, and bridal sets with dimensions and materials.'
      },
      {
        step: '3',
        title: 'Collect Orders & Receive UPI Payments',
        desc: 'Share your store link on Instagram, Pinterest, and WhatsApp to start selling nationwide.'
      }
    ],
    faqList: [
      {
        q: 'Can I sell imitation and artificial jewelry on Ownezzy?',
        a: 'Absolutely! Thousands of independent artisans and imitation jewelry retailers use Ownezzy to sell Kundan, Meenakari, American Diamond, and oxidized silver jewelry.'
      },
      {
        q: 'How do I handle COD return risks for jewelry?',
        a: 'Ownezzy lets you enforce prepaid-only orders or set a mandatory partial deposit before dispatching COD jewelry orders.'
      },
      {
        q: 'Can customers request customization via WhatsApp?',
        a: 'Yes, every product page includes a direct "Inquire on WhatsApp" button so customers can ask about custom necklace lengths, stone colors, or bridal sets.'
      },
      {
        q: 'What are the charges for launching my jewelry store?',
        a: 'You get a full 7-day free trial with zero setup fees and 0% commission on your sales.'
      }
    ],
    relatedNiches: ['clothing-website', 'beauty-cosmetics-website', 'gifts-hampers-website', 'footwear-website']
  },

  {
    slug: 'phone-cases-website',
    nicheName: 'Phone Cases & Tech Accessories',
    shortName: 'Phone Cases',
    icon: '📱',
    badge: 'Fast-Moving Consumer Tech',
    headline: 'How to Create a Phone Cases Website in 30 Seconds',
    subheadline: 'Sell custom phone cases, MagSafe accessories, tempered glass, cables, and skins online. Built-in device model selector for iPhone, Samsung, OnePlus, and Xiaomi.',
    metaTitle: 'How to Create a Phone Cases Website | Mobile Accessories Store – Ownezzy',
    metaDescription: 'Create a phone cases & mobile accessories website with phone model selectors, bundle discounts, WhatsApp checkout & 0% commission. Start free 7-day trial today.',
    targetKeywords: [
      'how to create phone cases website',
      'phone case website builder',
      'sell mobile covers online india',
      'custom phone case store',
      'mobile accessories ecommerce'
    ],
    templateId: 'tech',
    demoSubdomain: 'cases',
    accentColor: '#2563eb',
    heroStats: {
      activeStores: '1,200+',
      avgSetupTime: '30 seconds',
      commission: '0%',
      rating: '4.8/5'
    },
    keyBenefits: [
      {
        title: 'Phone Model Selector',
        desc: 'Let customers easily pick their exact phone model (iPhone 16, S24 Ultra, OnePlus 12) from an organized dropdown.',
        icon: '📱'
      },
      {
        title: 'Bundle & Combo Offers',
        desc: 'Boost average order value with "Buy Case + Tempered Glass & Save 20%" automatic bundle discounts.',
        icon: '🏷️'
      },
      {
        title: 'Custom Photo Print Upload',
        desc: 'Allow buyers to upload personal photos or custom text directly for customized phone case printing.',
        icon: '🖼️'
      },
      {
        title: 'Low Return Rate Verification',
        desc: 'Automated order verification via WhatsApp ensures customers picked the right phone model before shipping.',
        icon: '✅'
      }
    ],
    featuresList: [
      'Pre-configured phone model hierarchy covering 100+ Indian smartphone models',
      'Drop-test resistance badges, MagSafe compatibility icons, and material specs',
      'Direct WhatsApp checkout with automated order receipts and address collection',
      'Ultra-fast mobile loading for high-converting social media ad traffic',
      'Support for custom skins, watch straps, AirPods cases, and charging cables',
      'Keep 100% of your margins with zero commission on all transactions'
    ],
    howToSteps: [
      {
        step: '1',
        title: 'Pick the Tech & Cases Theme',
        desc: 'Select the CaseVault theme designed for modern gadgets, bold imagery, and clean filters.'
      },
      {
        step: '2',
        title: 'Add Designs & Supported Phone Models',
        desc: 'Upload your aesthetic print designs and specify supported Apple, Samsung, and Android devices.'
      },
      {
        step: '3',
        title: 'Drive Traffic & Fulfill Orders',
        desc: 'Run Instagram ads or post reels with your store link and ship cases nationwide.'
      }
    ],
    faqList: [
      {
        q: 'Can customers upload custom photos for printed phone covers?',
        a: 'Yes, customers can attach photos or enter custom name/monogram instructions directly during checkout or on WhatsApp.'
      },
      {
        q: 'How do I avoid customers ordering the wrong phone model?',
        a: 'Ownezzy includes clear model dropdowns and automatically includes the exact phone model name in the WhatsApp confirmation message for easy double-checking.'
      },
      {
        q: 'Can I sell screen protectors and charging cables too?',
        a: 'Yes! You can organize products into categories like Hard Cases, Glass Cases, Screen Protectors, MagSafe Chargers, and Watch Bands.'
      },
      {
        q: 'What is the transaction fee on sales?',
        a: '0%. Ownezzy charges zero commission fees on your sales, saving you thousands compared to Amazon or Shopify app fees.'
      }
    ],
    relatedNiches: ['clothing-website', 'gifts-hampers-website', 'crochet-handicrafts-website']
  },

  {
    slug: 'grocery-website',
    nicheName: 'Online Grocery & Kirana Store',
    shortName: 'Grocery',
    icon: '🛒',
    badge: 'Essential Daily Goods',
    headline: 'How to Create an Online Grocery Website in 30 Seconds',
    subheadline: 'Take your local Kirana store or organic food business online. Accept quick repeat orders on WhatsApp, offer local delivery slots, and take instant UPI payments.',
    metaTitle: 'How to Create an Online Grocery Website | Kirana Store Builder – Ownezzy',
    metaDescription: 'Create a local grocery & Kirana store website with WhatsApp quick ordering, delivery time slots, weight options & UPI checkout. Start free 7-day trial today.',
    targetKeywords: [
      'how to create grocery website',
      'online kirana store builder',
      'sell groceries online india',
      'local grocery delivery website',
      'hyperlocal store builder'
    ],
    templateId: 'grocery',
    demoSubdomain: 'grocery',
    accentColor: '#ea580c',
    heroStats: {
      activeStores: '3,100+',
      avgSetupTime: '30 seconds',
      commission: '0%',
      rating: '4.9/5'
    },
    keyBenefits: [
      {
        title: 'Weight & Pack Variants',
        desc: 'Sell grains, dry fruits, and pulses by weight: 500g, 1kg, 5kg, 10kg with automatic price calculation.',
        icon: '⚖️'
      },
      {
        title: '1-Click WhatsApp Quick Reorder',
        desc: 'Customers can reorder their monthly grocery basket in 1 tap directly through WhatsApp.',
        icon: '📲'
      },
      {
        title: 'Neighborhood Delivery Slots',
        desc: 'Define delivery areas by pincode or local radius with morning and evening delivery time slots.',
        icon: '🛵'
      },
      {
        title: 'Direct UPI to Your Bank',
        desc: 'Receive money straight into your shop current/savings account without any aggregator hold.',
        icon: '🏦'
      }
    ],
    featuresList: [
      'Ready-to-use Kirana catalogue with daily essentials, snacks, and staples',
      'Minimum order value settings for free home delivery',
      'Customer address and landmark collection for smooth delivery boy dispatch',
      'Printed thermal invoice and WhatsApp delivery receipts',
      'Real-time inventory management to prevent stockouts',
      '0% commission—save on aggregator delivery commissions'
    ],
    howToSteps: [
      {
        step: '1',
        title: 'Select DailyBasket Grocery Theme',
        desc: 'Choose our clean grocery layout optimized for quick item additions and category browsing.'
      },
      {
        step: '2',
        title: 'Add Your Items & Daily Prices',
        desc: 'Import common grocery essentials and set your competitive local prices and pack weights.'
      },
      {
        step: '3',
        title: 'Share with Neighborhood Customers',
        desc: 'Distribute your website link via WhatsApp groups and flyers for instant local orders.'
      }
    ],
    faqList: [
      {
        q: 'Can I set a minimum order amount for free delivery?',
        a: 'Yes, you can configure minimum order values (e.g. Free Delivery above ₹499) and charge a nominal delivery fee for smaller carts.'
      },
      {
        q: 'Can customers order by sending a handwritten list on WhatsApp?',
        a: 'Yes, the storefront features an easy "Upload Handwritten Grocery List via WhatsApp" option for elderly or busy customers.'
      },
      {
        q: 'Can I restrict delivery to only my local pincodes?',
        a: 'Yes! You can specify the exact pincodes or neighborhoods you serve.'
      },
      {
        q: 'How fast can I start taking orders?',
        a: 'Your grocery website will be ready to take orders in under 30 seconds after entering your store name.'
      }
    ],
    relatedNiches: ['bakery-cakes-website', 'beauty-cosmetics-website', 'pet-supplies-website']
  },

  {
    slug: 'footwear-website',
    nicheName: 'Footwear & Handcrafted Juttis',
    shortName: 'Footwear',
    icon: '👡',
    badge: 'Artisan & Footwear Niche',
    headline: 'How to Create a Footwear Website in 30 Seconds',
    subheadline: 'Sell handcrafted Punjabi juttis, bridal mojaris, sneakers, flats, and double-cushioned slides online. Complete with Indian & European shoe size converters.',
    metaTitle: 'How to Create a Footwear Website | Punjabi Jutti & Shoe Store – Ownezzy',
    metaDescription: 'Create an online footwear & shoe store website with shoe size charts, cushioned comfort guides, WhatsApp checkout & 0% commission. Start free 7-day trial.',
    targetKeywords: [
      'how to create footwear website',
      'sell juttis online india',
      'shoe store website builder',
      'handcrafted mojari online shop',
      'footwear ecommerce platform'
    ],
    templateId: 'footwear',
    demoSubdomain: 'footwear',
    accentColor: '#a21caf',
    heroStats: {
      activeStores: '950+',
      avgSetupTime: '30 seconds',
      commission: '0%',
      rating: '4.8/5'
    },
    keyBenefits: [
      {
        title: 'Shoe Size Chart & Converter',
        desc: 'Interactive size guide with Indian (IND/UK), US, and EU shoe sizes plus foot length in centimeters.',
        icon: '👟'
      },
      {
        title: 'Double-Cushion Comfort Highlights',
        desc: 'Showcase memory foam padding, genuine leather soles, and bite-free artisan embroidery.',
        icon: '✨'
      },
      {
        title: 'Bridal & Custom Mojaris',
        desc: 'Direct WhatsApp integration for custom matching juttis to bridal lehenga embroidery.',
        icon: '👰'
      },
      {
        title: 'Hassle-Free Size Exchanges',
        desc: 'Build buyer confidence with clear size replacement policies and automated return ticket requests.',
        icon: '🔄'
      }
    ],
    featuresList: [
      'Designed specifically for footwear: heel height, sole material, and occasion tags',
      'High-res embroidery detail zoom for zardozi, mirror work, and dabka art',
      'Automated pincode delivery estimates across India',
      'Prepaid UPI discount triggers to minimize COD return costs',
      'Instagram shopping bio integration for festive and wedding seasons',
      'Zero marketplace listing deductions—keep 100% of your sales'
    ],
    howToSteps: [
      {
        step: '1',
        title: 'Pick SoleChic Footwear Theme',
        desc: 'Choose our stylish footwear layout with size selector pills and high-impact hero banners.'
      },
      {
        step: '2',
        title: 'Upload Footwear Designs & Sizes',
        desc: 'Add sizes 35 through 42 with clear photos from top, side, and sole angles.'
      },
      {
        step: '3',
        title: 'Promote & Receive Prepaid Orders',
        desc: 'Share your store link on Instagram, post customer reviews, and accept UPI orders.'
      }
    ],
    faqList: [
      {
        q: 'How do customers pick their exact shoe size?',
        a: 'The product page displays clear size selector buttons (Size 36, 37, 38, etc.) along with an interactive Size Guide popup.'
      },
      {
        q: 'Can I offer size exchanges if the shoe doesn’t fit the customer?',
        a: 'Yes, you can specify your exchange policy on product pages and manage exchange requests easily over WhatsApp.'
      },
      {
        q: 'Do you charge transaction fees on sales?',
        a: 'Zero. Ownezzy never takes a percentage of your sales.'
      },
      {
        q: 'Can I sell men’s, women’s, and kids’ footwear together?',
        a: 'Yes! You can organize your store into Men, Women, Bridal, Casual, and Kids footwear categories.'
      }
    ],
    relatedNiches: ['clothing-website', 'jewelry-website', 'bags-website']
  },

  {
    slug: 'beauty-cosmetics-website',
    nicheName: 'Beauty, Skincare & Cosmetics',
    shortName: 'Beauty',
    icon: '🧼',
    badge: 'Organic & Skincare Boom',
    headline: 'How to Create a Beauty & Skincare Website in 30 Seconds',
    subheadline: 'Sell organic handmade soaps, botanical skincare, perfumes, lip tints, and hair oils online. Highlight natural ingredients, cruelty-free badges, and skin-type filters.',
    metaTitle: 'How to Create a Beauty Website | Skincare & Cosmetics Store – Ownezzy',
    metaDescription: 'Create an online beauty & skincare store with ingredient spotlights, skin type filters, WhatsApp ordering, 0% commission & UPI. Start free 7-day trial.',
    targetKeywords: [
      'how to create beauty website',
      'skincare website builder',
      'sell handmade soaps online',
      'organic cosmetics ecommerce store',
      'natural skincare online shop'
    ],
    templateId: 'wellness',
    demoSubdomain: 'soaps',
    accentColor: '#7e5aa2',
    heroStats: {
      activeStores: '1,650+',
      avgSetupTime: '30 seconds',
      commission: '0%',
      rating: '4.9/5'
    },
    keyBenefits: [
      {
        title: 'Ingredient & Benefit Badges',
        desc: 'Showcase 100% Organic, Paraben-Free, Cruelty-Free, and Ayurvedic certifications clearly.',
        icon: '🌿'
      },
      {
        title: 'Skin Type Filter',
        desc: 'Help buyers find the ideal product for Oily, Dry, Sensitive, Acne-Prone, or Combination skin.',
        icon: '✨'
      },
      {
        title: 'Gift Sets & Discovery Boxes',
        desc: 'Create mini trial kits and festive gift hampers with custom packaging options.',
        icon: '🎁'
      },
      {
        title: 'WhatsApp Skin Consultations',
        desc: 'Let customers chat directly with you to get personalized skincare routine recommendations.',
        icon: '💬'
      }
    ],
    featuresList: [
      'Clean, aesthetic cosmetic layout designed for organic and wellness brands',
      'Detailed "How to Use" and "Key Ingredients" tabs on every product page',
      'Customer review photo gallery showcasing genuine before/after transformations',
      'Automated WhatsApp shipping notifications with live tracking links',
      'Custom domain support with free SSL certificate',
      'Zero commission fees on all orders'
    ],
    howToSteps: [
      {
        step: '1',
        title: 'Choose the Lather & Bloom Theme',
        desc: 'Select our calming, elegant skincare theme tailored for handmade soaps and serums.'
      },
      {
        step: '2',
        title: 'Add Products & Ingredients',
        desc: 'Upload aesthetic bottle and soap photos with benefits and usage instructions.'
      },
      {
        step: '3',
        title: 'Sell Online & Grow Your Brand',
        desc: 'Link your store on Instagram and convert skincare enthusiasts into loyal repeat buyers.'
      }
    ],
    faqList: [
      {
        q: 'Can I sell homemade organic soaps and oils on Ownezzy?',
        a: 'Yes! Hundreds of artisanal home soapmakers and organic cosmetic founders run their direct-to-consumer store on Ownezzy.'
      },
      {
        q: 'Can I offer sample or trial sizes?',
        a: 'Yes, you can create variant options like 30ml, 50ml, 100ml, or create a "Discovery Sample Box" product.'
      },
      {
        q: 'How do customers pay?',
        a: 'Via UPI (GPay, PhonePe, Paytm), credit/debit cards, net banking, or Cash on Delivery.'
      },
      {
        q: 'Do I have to pay transaction commissions?',
        a: 'No. Ownezzy has zero transaction fees on all your orders.'
      }
    ],
    relatedNiches: ['clothing-website', 'jewelry-website', 'gifts-hampers-website', 'home-decor-website']
  },

  {
    slug: 'home-decor-website',
    nicheName: 'Home Decor & Handicrafts',
    shortName: 'Home Decor',
    icon: '🕯️',
    badge: 'Artisan & Living Niche',
    headline: 'How to Create a Home Decor Website in 30 Seconds',
    subheadline: 'Sell hand-poured soy wax candles, brass pooja urulis, ceramic planters, wall art, and aesthetic room decor online with high-res styling photos and instant UPI checkout.',
    metaTitle: 'How to Create a Home Decor Website | Candles & Brassware Store – Ownezzy',
    metaDescription: 'Create a home decor & handicraft store website with fragile item shipping options, WhatsApp ordering, 0% commission & UPI. Start free 7-day trial.',
    targetKeywords: [
      'how to create home decor website',
      'home decor website builder',
      'sell scented candles online',
      'brass uruli online store',
      'handicrafts ecommerce india'
    ],
    templateId: 'decor',
    demoSubdomain: 'decor',
    accentColor: '#d97706',
    heroStats: {
      activeStores: '1,400+',
      avgSetupTime: '30 seconds',
      commission: '0%',
      rating: '4.8/5'
    },
    keyBenefits: [
      {
        title: 'Fragile Packing Badges',
        desc: 'Reassure buyers with break-proof thermocol & bubble wrapping assurance notices on fragile pieces.',
        icon: '📦'
      },
      {
        title: 'Room Styling Inspirations',
        desc: 'Upload multi-image galleries showing how candles, vases, and brassware look in styled living rooms.',
        icon: '🛋️'
      },
      {
        title: 'Festive Pooja Bundles',
        desc: 'Create Diwali, housewarming, and wedding gift bundles with customizable brass diyas & urulis.',
        icon: '🪔'
      },
      {
        title: 'Direct WhatsApp Consultations',
        desc: 'Let interior stylists and homeowners inquire about custom sizes and bulk wedding orders.',
        icon: '💬'
      }
    ],
    featuresList: [
      'Rich aesthetic layout showcasing artisan craftsmanship and warm tones',
      'Dimensions and weight specifications on all items for accurate living space planning',
      'Automated invoice generation with fragility warnings for delivery partners',
      'Built-in coupon codes and festive seasonal sale banners',
      'Custom domain support with free SSL security',
      'Keep 100% of your retail margins with zero platform commission'
    ],
    howToSteps: [
      {
        step: '1',
        title: 'Choose the LuminaNest Decor Theme',
        desc: 'Select our warm, earthy theme designed for artisan candles, ceramics, and brassware.'
      },
      {
        step: '2',
        title: 'Add Styled Photos & Dimensions',
        desc: 'Upload well-lit room photos, item measurements (height, width), and scent profiles.'
      },
      {
        step: '3',
        title: 'Launch & Receive Orders',
        desc: 'Share your store link across Instagram and Pinterest to receive prepaid UPI orders.'
      }
    ],
    faqList: [
      {
        q: 'Can I sell scented soy candles with different fragrance variants?',
        a: 'Yes, you can create variant options such as Vanilla, Lavender, Sandalwood, and different jar sizes.'
      },
      {
        q: 'How do I handle shipping fragile brass and ceramics?',
        a: 'Ownezzy lets you add packing charges or offer insured express shipping partners like Shiprocket or Delhivery.'
      },
      {
        q: 'Can I sell bulk wedding favors and housewarming gifts?',
        a: 'Yes! Customers can place bulk orders or contact you with 1-click via WhatsApp.'
      },
      {
        q: 'What is the platform commission on sales?',
        a: '0%. Ownezzy never charges a percentage of your sales.'
      }
    ],
    relatedNiches: ['gifts-hampers-website', 'jewelry-website', 'crochet-handicrafts-website']
  },

  {
    slug: 'bakery-cakes-website',
    nicheName: 'Bakery, Cakes & Sweet Shop',
    shortName: 'Bakery',
    icon: '🎂',
    badge: 'Home Baker & Sweet Niche',
    headline: 'How to Create a Bakery & Cake Website in 30 Seconds',
    subheadline: 'Start taking online orders for custom birthday cakes, cupcakes, brownies, artisanal sourdough, and festive sweets. Includes delivery date selector and eggless/egg tags.',
    metaTitle: 'How to Create a Bakery Website | Cake & Sweet Shop Builder – Ownezzy',
    metaDescription: 'Create an online bakery & cake store with delivery date pickers, eggless tags, WhatsApp orders, 0% commission & UPI. Start free 7-day trial today.',
    targetKeywords: [
      'how to create bakery website',
      'cake shop website builder',
      'sell homemade cakes online',
      'online bakery store india',
      'home baker website builder'
    ],
    templateId: 'fresh',
    demoSubdomain: 'fresh',
    accentColor: '#db2777',
    heroStats: {
      activeStores: '1,500+',
      avgSetupTime: '30 seconds',
      commission: '0%',
      rating: '4.9/5'
    },
    keyBenefits: [
      {
        title: 'Delivery Date & Time Slot',
        desc: 'Customers pick their exact party delivery date and time slot so cakes arrive fresh.',
        icon: '📅'
      },
      {
        title: 'Eggless & Dietary Badges',
        desc: 'Clear 100% Eggless, Gluten-Free, Vegan, or Sugar-Free badges on every treat.',
        icon: '🌱'
      },
      {
        title: 'Custom Cake Name & Message',
        desc: 'Allow buyers to enter "Name on Cake" and custom birthday candle instructions at checkout.',
        icon: '✍️'
      },
      {
        title: 'Instant Advance UPI Payment',
        desc: 'Collect 100% advance or deposit via UPI to prevent last-minute cancellations of custom bakes.',
        icon: '💳'
      }
    ],
    featuresList: [
      'Mouth-watering photography showcase with cross-section filling views',
      'Weight variants (0.5 kg, 1 kg, 2 kg) with automatic price calculations',
      'Direct WhatsApp order link with custom cake reference photo upload',
      'Local delivery radius restriction by pincodes',
      'Printable order tickets with customer delivery time and landmark',
      '0% commission on orders—save high commission fees from food aggregators'
    ],
    howToSteps: [
      {
        step: '1',
        title: 'Select the Fresh Bakery Theme',
        desc: 'Choose our delightful, appetite-inducing theme optimized for sweet treats and cakes.'
      },
      {
        step: '2',
        title: 'Add Flavors, Sizes & Prices',
        desc: 'Upload mouth-watering cake photos with flavor descriptions and weight options.'
      },
      {
        step: '3',
        title: 'Take Advance Orders On WhatsApp',
        desc: 'Share your link with neighborhood customers and accept pre-orders for parties and festivals.'
      }
    ],
    faqList: [
      {
        q: 'Can customers write a message to put on the cake?',
        a: 'Yes, every cake product includes a text box where the customer can type "Happy 25th Birthday Priya" and specify number candles.'
      },
      {
        q: 'Can I collect full advance payment before baking?',
        a: 'Yes! Ownezzy allows you to require full advance payment via UPI so you never lose money on customized bakes.'
      },
      {
        q: 'Can I restrict deliveries to only my city?',
        a: 'Yes, you can specify your local delivery pincodes so only nearby customers can place cake orders.'
      },
      {
        q: 'How much does it cost to get started?',
        a: 'You get a 7-day free trial with zero setup fees and 0% commission.'
      }
    ],
    relatedNiches: ['grocery-website', 'gifts-hampers-website', 'home-decor-website']
  },

  {
    slug: 'gifts-hampers-website',
    nicheName: 'Gift Store & Hampers',
    shortName: 'Gifts & Hampers',
    icon: '🎁',
    badge: 'Festive & Occasion Favorite',
    headline: 'How to Create a Gift Store Website in 30 Seconds',
    subheadline: 'Sell personalized gift hampers, corporate gifts, wedding return gifts, birthday boxes, and custom photo frames online with direct WhatsApp ordering and pan-India shipping.',
    metaTitle: 'How to Create a Gift Store Website | Hampers & Favors – Ownezzy',
    metaDescription: 'Create an online gift hampers & return gifts website with custom note cards, WhatsApp checkout, 0% commission & UPI. Start free 7-day trial today.',
    targetKeywords: [
      'how to create gift store website',
      'gift hamper website builder',
      'sell personalized gifts online',
      'wedding return gifts online shop',
      'corporate gift hampers ecommerce'
    ],
    templateId: 'gifts',
    demoSubdomain: 'gifts',
    accentColor: '#be185d',
    heroStats: {
      activeStores: '2,100+',
      avgSetupTime: '30 seconds',
      commission: '0%',
      rating: '4.9/5'
    },
    keyBenefits: [
      {
        title: 'Custom Note & Gifting Box',
        desc: 'Customers can enter personal greeting card messages and choose luxury gift box wrappings.',
        icon: '💌'
      },
      {
        title: 'Bulk Wedding & Return Favors',
        desc: 'Volume pricing tables for 50+, 100+, 200+ gift boxes with automatic tiered discounts.',
        icon: '🎊'
      },
      {
        title: 'Personalized Photo Upload',
        desc: 'Let customers upload pictures for engraved frames, custom mugs, and keepsake plaques.',
        icon: '🖼️'
      },
      {
        title: 'Send Directly to Recipient',
        desc: 'Separate billing and shipping addresses with hidden invoice pricing for surprise gifts.',
        icon: '🚚'
      }
    ],
    featuresList: [
      'Celebratory, joyful layout tailored for Diwali, Rakhi, Valentine, and wedding gifts',
      'Occasion filters: Birthday, Anniversary, Wedding, Housewarming, and Corporate',
      'Direct WhatsApp chat for corporate bulk quotation requests',
      'Automated dispatch tracking updates sent to sender and receiver',
      'Custom domain support with free SSL certificate',
      'Keep 100% of your gift profits with zero platform commission'
    ],
    howToSteps: [
      {
        step: '1',
        title: 'Pick the Giftly Theme',
        desc: 'Choose our vibrant gift layout built for hampers, personalized items, and celebrations.'
      },
      {
        step: '2',
        title: 'Upload Hampers & Custom Options',
        desc: 'List your gift combinations, contents list, and personal greeting message options.'
      },
      {
        step: '3',
        title: 'Collect Orders & Ship Pan-India',
        desc: 'Share your store link on Instagram and WhatsApp status to capture holiday gift surges.'
      }
    ],
    faqList: [
      {
        q: 'Can customers send a gift directly to another person’s address?',
        a: 'Yes! Customers can enter the recipient’s address while paying with their own UPI, and choose to hide the price invoice.'
      },
      {
        q: 'Can I accept bulk orders for corporate gifts or weddings?',
        a: 'Yes, every product includes a direct "Bulk Inquiry via WhatsApp" button for custom quotations.'
      },
      {
        q: 'Can customers upload photos for personalized keepsakes?',
        a: 'Yes, buyers can attach photos directly or send them on WhatsApp after placing the order.'
      },
      {
        q: 'What are the transaction charges?',
        a: '0%. Ownezzy does not charge any transaction fees.'
      }
    ],
    relatedNiches: ['home-decor-website', 'jewelry-website', 'crochet-handicrafts-website']
  },

  {
    slug: 'pet-supplies-website',
    nicheName: 'Pet Store & Pet Supplies',
    shortName: 'Pet Store',
    icon: '🐾',
    badge: 'High-Loyalty Pet Niche',
    headline: 'How to Create a Pet Store Website in 30 Seconds',
    subheadline: 'Sell premium pet food, treats, toys, grooming products, leashes, and aquarium supplies online with repeat order subscriptions and instant UPI payments.',
    metaTitle: 'How to Create a Pet Store Website | Pet Supplies Online – Ownezzy',
    metaDescription: 'Create a pet store website for pet food, grooming & accessories with repeat WhatsApp ordering, 0% commission & UPI. Start free 7-day trial today.',
    targetKeywords: [
      'how to create pet store website',
      'pet supplies website builder',
      'sell pet food online india',
      'dog food online store',
      'cat accessories ecommerce'
    ],
    templateId: 'pets',
    demoSubdomain: 'pet',
    accentColor: '#807a12',
    heroStats: {
      activeStores: '780+',
      avgSetupTime: '30 seconds',
      commission: '0%',
      rating: '4.8/5'
    },
    keyBenefits: [
      {
        title: 'Pet Breed & Size Filters',
        desc: 'Help pet parents filter by Dog, Cat, Bird, Fish, Puppy, and Adult life stages.',
        icon: '🐕'
      },
      {
        title: 'Weight & Pack Sizes',
        desc: 'List kibble bags from 1.2kg up to 20kg bags with automated shipping rate calculation.',
        icon: '⚖️'
      },
      {
        title: 'Monthly Auto-Reorder on WhatsApp',
        desc: 'Pet owners can reorder their regular dog/cat food in 1 tap without searching again.',
        icon: '🔄'
      },
      {
        title: 'Zero Commission Fees',
        desc: 'Keep high margins on pet accessories and branded food with zero marketplace cuts.',
        icon: '🏷️'
      }
    ],
    featuresList: [
      'Fun, welcoming pet design with high-contrast categories for Dogs, Cats, and Aquariums',
      'Expiry date and nutritional ingredient details on food product pages',
      'Fast delivery estimates and door-to-door courier integration',
      'Custom domain support with free SSL security',
      'Built-in coupon codes and bundle savings on food + treats combos',
      'Zero commission fees on every transaction'
    ],
    howToSteps: [
      {
        step: '1',
        title: 'Choose the PawPantry Pet Theme',
        desc: 'Select our cheerful layout with clear navigation for dogs, cats, toys, and grooming.'
      },
      {
        step: '2',
        title: 'Add Pet Food Brands & Accessories',
        desc: 'List food bags, collars, bowls, and toys with breed sizes and dietary benefits.'
      },
      {
        step: '3',
        title: 'Serve Pet Parents Nationwide',
        desc: 'Promote your store link to pet communities and fulfill repeat orders reliably.'
      }
    ],
    faqList: [
      {
        q: 'Can I sell both dog food and accessories?',
        a: 'Yes, you can organize products into Dog Food, Cat Food, Grooming, Toys, Leashes, and Aquarium.'
      },
      {
        q: 'Can customers reorder every month easily?',
        a: 'Yes, customers can tap "Reorder on WhatsApp" to get their monthly pet food bag dispatched in seconds.'
      },
      {
        q: 'Can I sell live fish or aquarium plants?',
        a: 'Yes! Many ornamental fish breeders and aquarium shops use Ownezzy with local pincode delivery.'
      },
      {
        q: 'What is the fee to launch?',
        a: '7-day free trial with zero setup cost and 0% commission.'
      }
    ],
    relatedNiches: ['grocery-website', 'home-decor-website']
  },

  {
    slug: 'crochet-handicrafts-website',
    nicheName: 'Crochet & Resin Art Keepsakes',
    shortName: 'Crochet & Resin',
    icon: '🌸',
    badge: 'Artisan & Creator Economy',
    headline: 'How to Create a Crochet & Resin Art Website in 30 Seconds',
    subheadline: 'Sell handmade crochet flower bouquets, varmala wedding flower preservation clocks, resin jewelry, and amigurumi plushies online with WhatsApp customization.',
    metaTitle: 'How to Create a Crochet & Resin Art Website | Handmade Crafts – Ownezzy',
    metaDescription: 'Create a crochet & resin art website with custom photo uploads, flower preservation orders, 0% commission & UPI. Start free 7-day trial today.',
    targetKeywords: [
      'how to create crochet website',
      'sell resin art online',
      'handmade crafts website builder',
      'crochet bouquet online shop',
      'wedding varmala preservation store'
    ],
    templateId: 'crochet',
    demoSubdomain: 'crochet',
    accentColor: '#7c3aed',
    heroStats: {
      activeStores: '1,100+',
      avgSetupTime: '30 seconds',
      commission: '0%',
      rating: '4.9/5'
    },
    keyBenefits: [
      {
        title: 'Custom Varmala Preservation',
        desc: 'Guides customers on how to dry & courier their wedding varmala for preservation clocks and platters.',
        icon: '🕰️'
      },
      {
        title: 'Everlasting Crochet Bouquets',
        desc: 'Display handmade yarn flower bouquets that never wither for birthdays and anniversaries.',
        icon: '💐'
      },
      {
        title: '1-on-1 WhatsApp Customization',
        desc: 'Discuss thread colors, custom names, and packaging details directly with buyers on WhatsApp.',
        icon: '💬'
      },
      {
        title: '100% Advance Payment via UPI',
        desc: 'Safeguard your precious handcrafted labor with mandatory advance UPI payment before starting work.',
        icon: '🛡️'
      }
    ],
    featuresList: [
      'Artisan-centric aesthetic highlighting hours of intricate handmade work',
      'Multi-photo galleries showing resin clarity, bubble-free finish, and yarn textures',
      'Custom lead time notices (e.g. "Handcrafted in 7 days before dispatch")',
      'Support for personalized nameplates, dashboard bobbleheads, and keychains',
      'Direct Instagram Bio link integration to convert craft reels into paid orders',
      'Zero commission on sales—100% of your artistic value stays with you'
    ],
    howToSteps: [
      {
        step: '1',
        title: 'Pick YarnBlooms & Resin Theme',
        desc: 'Choose our aesthetic craft theme created specifically for handmade fiber art and resin.'
      },
      {
        step: '2',
        title: 'Upload Your Artisan Creations',
        desc: 'Add high-res photos of bouquets, clocks, and dolls with dimensions and making times.'
      },
      {
        step: '3',
        title: 'Take Orders & Advance UPI Payments',
        desc: 'Put your store link in your Instagram bio to receive confirmed orders automatically.'
      }
    ],
    faqList: [
      {
        q: 'Can customers send their own wedding flowers for resin preservation?',
        a: 'Yes! Product pages include clear step-by-step instructions for drying and mailing wedding garlands.'
      },
      {
        q: 'Can I set a 7 to 10 day making time for handmade orders?',
        a: 'Yes, you can clearly display your processing time so customers know when their handmade piece will ship.'
      },
      {
        q: 'Do you charge listing fees or commissions?',
        a: 'Zero. Unlike Etsy which charges hefty listing and conversion fees, Ownezzy has 0% commission.'
      },
      {
        q: 'Can customers customize colors of crochet flowers?',
        a: 'Yes, buyers can specify flower colors during checkout or tap the WhatsApp button to chat with you.'
      }
    ],
    relatedNiches: ['gifts-hampers-website', 'home-decor-website', 'jewelry-website']
  },

  {
    slug: 'kids-ethnic-wear-website',
    nicheName: 'Kids Ethnic Wear & Pattu Pavadai',
    shortName: 'Kids Ethnic',
    icon: '🧒',
    badge: 'Festive & Traditional Kids Wear',
    headline: 'How to Create a Kids Ethnic Wear Website in 30 Seconds',
    subheadline: 'Sell South Indian Pattu Pavadai, silk dhotis, lehengas, sherwanis, and naming ceremony sets online with pure cotton lining guarantees and age-based size charts.',
    metaTitle: 'How to Create a Kids Ethnic Wear Website | Pattu Pavadai – Ownezzy',
    metaDescription: 'Create a kids ethnic wear website with age-based size charts, pure cotton lining badges, WhatsApp orders & 0% commission. Start free 7-day trial today.',
    targetKeywords: [
      'how to create kids ethnic wear website',
      'pattu pavadai online store',
      'kids traditional wear website builder',
      'children festive dress online',
      'dhoti kurta for kids ecommerce'
    ],
    templateId: 'pattu',
    demoSubdomain: 'pattu',
    accentColor: '#db2777',
    heroStats: {
      activeStores: '820+',
      avgSetupTime: '30 seconds',
      commission: '0%',
      rating: '4.9/5'
    },
    keyBenefits: [
      {
        title: 'Age-Wise Size Selector',
        desc: 'Easy size categories: 0-6M, 6-12M, 1-2Y, 3-4Y up to 14Y with chest and skirt length in inches.',
        icon: '📏'
      },
      {
        title: 'Pure Cotton Lining Assurance',
        desc: 'Reassure mothers with badges highlighting 100% breathable pure cotton lining for baby skin comfort.',
        icon: '👶'
      },
      {
        title: 'Festive & Naming Ceremony Sets',
        desc: 'Special collections for Ayush Homa, Naming Ceremony, Pongal, Diwali, and wedding functions.',
        icon: '🪔'
      },
      {
        title: 'Direct WhatsApp Sizing Support',
        desc: 'Let parents confirm custom sleeve lengths or chest measurements directly before ordering.',
        icon: '💬'
      }
    ],
    featuresList: [
      'Vibrant festive layout showcasing rich Zari borders and silk textures',
      'High-res photo galleries with real baby model try-on shots',
      'Automated WhatsApp shipping notifications with live courier tracking',
      'Custom domain support with free SSL certificate',
      'Prepaid UPI discount incentives to lower COD returns',
      'Zero platform commission—keep 100% of your boutique profits'
    ],
    howToSteps: [
      {
        step: '1',
        title: 'Select KuttyPattu Ethnic Theme',
        desc: 'Choose our cheerful festive layout crafted for traditional South Indian and North Indian kids wear.'
      },
      {
        step: '2',
        title: 'Add Collections & Age Sizes',
        desc: 'Upload bright photos of pattu pavadai and dhotis with age sizes and measurements.'
      },
      {
        step: '3',
        title: 'Receive Orders & Grow Nationwide',
        desc: 'Share your store link on WhatsApp parenting groups and Instagram to get orders.'
      }
    ],
    faqList: [
      {
        q: 'Can parents select sizes by child’s age in months or years?',
        a: 'Yes, product pages feature clear age selectors like 0-6 Months, 1-2 Years, 3-4 Years, along with a detailed measurement chart.'
      },
      {
        q: 'Can I mention that the dress has pure cotton lining for soft baby skin?',
        a: 'Yes, every product highlights fabric composition and comfort lining badges prominently.'
      },
      {
        q: 'Can I sell matching sibling or mom-and-me sets?',
        a: 'Yes! You can create bundle products or matching sets easily.'
      },
      {
        q: 'What is the commission per order?',
        a: '0%. Ownezzy charges zero transaction commission on all your sales.'
      }
    ],
    relatedNiches: ['clothing-website', 'jewelry-website', 'gifts-hampers-website']
  }
];

export const getNicheBySlug = (slug) => {
  if (!slug) return NICHES[0];
  const clean = slug.toLowerCase().trim().replace(/\/+$/, '');
  return NICHES.find((n) => n.slug === clean) || NICHES.find((n) => n.slug === `${clean}-website`) || NICHES[0];
};
